import { inject, Injectable } from '@angular/core';
import { lastValueFrom } from 'rxjs';
import { Announcement } from '../../../api-v3/models/announcement';
import { PublicService as BasicPublicService } from '../../../api-v3/services/public.service';
import { PublicService as AddonsPublicService } from '../../../api/addons-v3/services/public.service';

export type GlobalAnnouncementSource = 'basic' | 'addons';

export type DisplayGlobalAnnouncement = Omit<Announcement, '_key'> & {
    _key?: string;
    identity: string;
    source: GlobalAnnouncementSource;
};

export interface GlobalAnnouncementsResult {
    announcements: DisplayGlobalAnnouncement[];
    hasFailures: boolean;
}

@Injectable({ providedIn: 'root' })
export class GlobalAnnouncementsService {
    private basicPublicService = inject(BasicPublicService);
    private addonsPublicService = inject(AddonsPublicService);

    async getGlobalAnnouncements(): Promise<GlobalAnnouncementsResult> {
        const [basicResult, addonsResult] = await Promise.allSettled([
            lastValueFrom(this.basicPublicService.getGlobalAnnouncements()),
            lastValueFrom(this.addonsPublicService.getGlobalAnnouncements()),
        ]);

        const basicAnnouncements =
            basicResult.status === 'fulfilled'
                ? basicResult.value.map((announcement, index) => ({
                      ...announcement,
                      identity: announcement._key ?? `basic:${index}`,
                      source: 'basic' as const,
                  }))
                : [];
        const addonsAnnouncements =
            addonsResult.status === 'fulfilled'
                ? addonsResult.value.map(announcement => ({
                      ...announcement,
                      identity: `addons:${announcement._key}`,
                      source: 'addons' as const,
                  }))
                : [];

        return {
            announcements: [...basicAnnouncements, ...addonsAnnouncements],
            hasFailures:
                basicResult.status === 'rejected' ||
                addonsResult.status === 'rejected',
        };
    }
}
